import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  Clock,
  Timer,
  FileText,
  File,
  Cpu,
  Server,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Play,
  FileQuestion,
  XCircle,
  CloudOff,
  HardDrive,
  Cloud,
  Layers,
  Trash2
} from 'lucide-react';
import { GroupedExtractionTable } from './GroupedExtractionTable';
import type { ExtractionGroupingMode } from '@/types/extraction-grouping';
import {
  useExtractionData,
  useEncompassExtractionData,
  type DocumentWithExtraction,
  type EncompassAttachmentWithExtraction,
  type FilterType,
  type ExtractionStatus,
  type GPUMetrics
} from '@/hooks/useExtractionData';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  queueDocumentForProcessing,
  queueDocumentsForProcessing,
  queueEncompassAttachmentForProcessing,
  queueEncompassAttachmentsForProcessing
} from '@/services/loanDocumentService';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { DOCUMENT_TYPE_LABELS } from '@/types/loan-documents';
import { useToast } from '@/hooks/use-toast';

interface DataExtractionTabProps {
  loanId: string;
}

type SourceType = 'lendwise' | 'encompass';

// Format milliseconds to human readable
function formatMs(ms: number | null | undefined): string {
  if (!ms) {
return '-';
}
  if (ms < 1000) {
return `${ms}ms`;
}
  return `${(ms / 1000).toFixed(1)}s`;
}

// Format character count to human readable
function formatCharCount(chars: number | null | undefined): string {
  if (!chars) {
return '-';
}
  if (chars < 1000) {
return `${chars}`;
}
  if (chars < 1000000) {
return `${(chars / 1000).toFixed(1)}K`;
}
  return `${(chars / 1000000).toFixed(2)}M`;
}

// Format file size
function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) {
return '-';
}
  if (bytes < 1024) {
return `${bytes}B`;
}
  if (bytes < 1024 * 1024) {
return `${(bytes / 1024).toFixed(1)}KB`;
}
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

// Status badge component
function StatusBadge({ status }: { status: ExtractionStatus }) {
  const statusConfig: Record<
    ExtractionStatus,
    { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof Loader2 }
  > = {
    unprocessed: { label: 'Not Processed', variant: 'outline', icon: FileQuestion },
    pending: { label: 'Queued', variant: 'secondary', icon: Clock },
    extracting: { label: 'Extracting', variant: 'secondary', icon: Loader2 },
    extracted: { label: 'Extracted', variant: 'secondary', icon: CheckCircle2 },
    classifying: { label: 'Classifying', variant: 'secondary', icon: Loader2 },
    classified: { label: 'Classified', variant: 'secondary', icon: CheckCircle2 },
    splitting: { label: 'Splitting', variant: 'secondary', icon: Loader2 },
    completed: { label: 'Completed', variant: 'default', icon: CheckCircle2 },
    failed: { label: 'Failed', variant: 'destructive', icon: AlertCircle }
  };

  const config = statusConfig[status] || statusConfig.unprocessed;
  const Icon = config.icon;
  const isAnimated = ['extracting', 'classifying', 'splitting'].includes(status);

  return (
    <Badge variant={config.variant} className="gap-1">
      <Icon className={cn('h-3 w-3', isAnimated && 'animate-spin')} />
      {config.label}
    </Badge>
  );
}

// Download status badge for Encompass
function DownloadStatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle2 }> = {
    downloaded: { label: 'Downloaded', variant: 'default', icon: CheckCircle2 },
    downloading: { label: 'Downloading', variant: 'secondary', icon: Loader2 },
    pending: { label: 'Pending', variant: 'outline', icon: Clock },
    pending_worker: { label: 'Processing', variant: 'secondary', icon: Loader2 },
    failed: { label: 'Failed', variant: 'destructive', icon: AlertCircle },
    unavailable: { label: 'Unavailable', variant: 'outline', icon: CloudOff },
    skipped: { label: 'Skipped', variant: 'outline', icon: File },
    deleted: { label: 'Deleted', variant: 'outline', icon: Trash2 }
  };

  const cfg = config[status] || config.pending;
  const Icon = cfg.icon;
  const isAnimated = status === 'downloading';

  return (
    <Badge variant={cfg.variant} className="gap-1 text-xs">
      <Icon className={cn('h-3 w-3', isAnimated && 'animate-spin')} />
      {cfg.label}
    </Badge>
  );
}

// GPU badge component
function GpuBadge({ gpuId }: { gpuId: string | null | undefined }) {
  if (!gpuId) {
return <span className="text-muted-foreground">-</span>;
}

  const gpuConfig: Record<string, { label: string; color: string }> = {
    gpu1: { label: 'GPU1 (5070)', color: 'bg-purple-100 text-purple-800' },
    gpu2: { label: 'GPU2 (3080)', color: 'bg-blue-100 text-blue-800' }
  };

  const config = gpuConfig[gpuId] || { label: gpuId, color: 'bg-gray-100 text-gray-800' };

  return <Badge variant="outline" className={cn('text-xs', config.color)}>{config.label}</Badge>;
}

// Metric card with GPU hover tooltip
function MetricCard({
  title,
  value,
  icon,
  highlight,
  gpuStats
}: {
  title: string;
  value: React.ReactNode;
  icon?: React.ReactNode;
  highlight?: 'warning' | 'success' | 'error';
  gpuStats: GPUMetrics[];
}) {
  const knownGpus = gpuStats.filter(g => g.gpu_id !== 'unknown');

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <Card className={cn(
            "cursor-pointer transition-shadow hover:shadow-md",
            highlight === 'warning' && 'border-orange-200 bg-orange-50',
            highlight === 'error' && 'border-red-200 bg-red-50'
          )}>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                {icon}
                {title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {value}
            </CardContent>
          </Card>
        </TooltipTrigger>
        <TooltipContent className="w-64 p-3" side="bottom">
          <div className="space-y-2">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <Cpu className="h-4 w-4" />
              GPU Breakdown
            </div>
            {knownGpus.length > 0 ? knownGpus.map(gpu => (
              <div key={gpu.gpu_id} className="border-t pt-2 text-xs">
                <div className="font-medium">{gpu.gpu_name}</div>
                <div className="text-muted-foreground">
                  {gpu.jobs_completed} jobs • {gpu.total_pages.toLocaleString()} pages • {formatMs(gpu.avg_per_page_ms)}/page
                </div>
              </div>
            )) : (
              <div className="text-xs text-muted-foreground">No GPU data available</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Expandable row component for Lendwise documents
function LendwiseDocumentRow({
  doc,
  onProcess,
  isProcessing
}: {
  doc: DocumentWithExtraction;
  onProcess: (docId: string) => void;
  isProcessing: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const hasExtractedText = doc.extracted_text && doc.extracted_text.length > 0;
  const canProcess = doc.status === 'unprocessed' || doc.status === 'failed';

  return (
    <>
      <TableRow className={cn(isOpen && 'bg-muted/50')}>
        <TableCell className="font-medium">
          {hasExtractedText ? (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1 p-0 h-auto font-medium hover:bg-transparent"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {doc.title || doc.original_filename || 'Untitled'}
            </Button>
          ) : (
            <span>{doc.title || doc.original_filename || 'Untitled'}</span>
          )}
        </TableCell>
        <TableCell>
          {doc.classification_result?.type ? (
            <Badge variant="outline" className="text-xs">
              {DOCUMENT_TYPE_LABELS[doc.classification_result.type] || doc.classification_result.type}
            </Badge>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>
        <TableCell>
          {doc.classification_result?.confidence !== undefined ? (
            <span
              className={cn(
                'font-medium',
                doc.classification_result.confidence >= 0.8
                  ? 'text-green-600'
                  : doc.classification_result.confidence >= 0.5
                  ? 'text-yellow-600'
                  : 'text-red-600'
              )}
            >
              {(doc.classification_result.confidence * 100).toFixed(0)}%
            </span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>
        <TableCell>
          <StatusBadge status={doc.status} />
        </TableCell>
        <TableCell>
          <GpuBadge gpuId={doc.processed_by_gpu} />
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {formatMs(doc.total_time_ms)}
        </TableCell>
        <TableCell className="text-right tabular-nums">
          {doc.total_pages || '-'}
        </TableCell>
        <TableCell className="text-right tabular-nums text-muted-foreground text-xs">
          {formatCharCount(doc.extracted_char_count)}
        </TableCell>
        <TableCell>
          {canProcess ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onProcess(doc.id)}
              disabled={isProcessing}
              className="h-7 text-xs"
            >
              {isProcessing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : doc.status === 'failed' ? (
                <>
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Retry
                </>
              ) : (
                <>
                  <Play className="h-3 w-3 mr-1" />
                  Process
                </>
              )}
            </Button>
          ) : (
            <span className="text-muted-foreground text-xs">
              {doc.queue_created_at
                ? formatDistanceToNow(new Date(doc.queue_created_at), { addSuffix: true })
                : '-'}
            </span>
          )}
        </TableCell>
      </TableRow>
      {hasExtractedText && isOpen && (
        <TableRow>
          <TableCell colSpan={10} className="bg-muted/30 p-0">
            <div className="p-4">
              <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                <FileText className="h-3 w-3" />
                Extracted Text ({formatCharCount(doc.extracted_char_count)} chars)
              </div>
              <ScrollArea className="h-48 w-full rounded border bg-white p-3">
                <pre className="text-xs whitespace-pre-wrap font-mono">{doc.extracted_text}</pre>
              </ScrollArea>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// Expandable row component for Encompass attachments
function EncompassAttachmentRow({
  attachment,
  onProcess,
  isProcessing
}: {
  attachment: EncompassAttachmentWithExtraction;
  onProcess: (attachmentId: string) => void;
  isProcessing: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const hasExtractedText = attachment.extracted_text && attachment.extracted_text.length > 0;
  const canProcess = attachment.canProcess && (attachment.status === 'unprocessed' || attachment.status === 'failed');

  // Build subtext parts
  const subtextParts: string[] = [formatFileSize(attachment.file_size)];
  if (attachment.processed_by_gpu) {
    subtextParts.push(attachment.processed_by_gpu.toUpperCase());
  }
  if (attachment.queue_created_at) {
    subtextParts.push(formatDistanceToNow(new Date(attachment.queue_created_at), { addSuffix: true }));
  }

  return (
    <>
      <TableRow className={cn(
        'even:bg-muted/30', // Zebra stripe
        isOpen && 'bg-blue-50',
        !attachment.canProcess && 'opacity-60'
      )}>
        {/* Document column - combined info */}
        <TableCell className="py-2">
          <div className="flex items-center gap-2">
            {hasExtractedText && (
              <Button
                variant="ghost"
                size="sm"
                className="p-0 h-auto hover:bg-transparent flex-shrink-0"
                onClick={() => setIsOpen(!isOpen)}
              >
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            )}
            <div className="min-w-0">
              <span className="font-medium text-sm truncate block" title={attachment.title || attachment.file_name || 'Untitled'}>
                {attachment.title || attachment.file_name || 'Untitled'}
              </span>
              <div className="text-xs text-muted-foreground">
                {subtextParts.join(' • ')}
              </div>
            </div>
          </div>
        </TableCell>

        {/* Status column - combined badges + action */}
        <TableCell className="py-2">
          <div className="flex items-center gap-1.5">
            <DownloadStatusBadge status={attachment.download_status} />
            {attachment.canProcess && <StatusBadge status={attachment.status} />}
            {canProcess && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onProcess(attachment.id)}
                disabled={isProcessing}
                className="h-6 px-2 text-xs ml-auto"
              >
                {isProcessing ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : attachment.status === 'failed' ? (
                  <RefreshCw className="h-3 w-3" />
                ) : (
                  <Play className="h-3 w-3" />
                )}
              </Button>
            )}
          </div>
        </TableCell>

        {/* Time */}
        <TableCell className="py-2 text-right tabular-nums text-sm">
          {formatMs(attachment.total_time_ms)}
        </TableCell>

        {/* Chars */}
        <TableCell className="py-2 text-right tabular-nums text-sm text-muted-foreground">
          {formatCharCount(attachment.extracted_char_count)}
        </TableCell>
      </TableRow>
      {hasExtractedText && isOpen && (
        <TableRow>
          <TableCell colSpan={4} className="bg-muted/30 p-0">
            <div className="p-4">
              <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-2">
                <FileText className="h-3 w-3" />
                Extracted Text ({formatCharCount(attachment.extracted_char_count)} chars)
              </div>
              <ScrollArea className="h-48 w-full rounded border bg-white p-3">
                <pre className="text-xs whitespace-pre-wrap font-mono">{attachment.extracted_text}</pre>
              </ScrollArea>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function DataExtractionTab({ loanId }: DataExtractionTabProps) {
  const { toast } = useToast();

  // Source selection
  const [source, setSource] = useState<SourceType>('encompass');

  // Grouping mode (flat or grouped by category) - default to grouped
  const [groupingMode, setGroupingMode] = useState<ExtractionGroupingMode>('groups');

  // Lendwise data
  const {
    documents: lendwiseDocs,
    metrics: lendwiseMetrics,
    loading: lendwiseLoading,
    error: lendwiseError,
    refresh: refreshLendwise,
    getFilteredDocuments,
    getGroupedFilteredDocuments
  } = useExtractionData({ loanId, limit: 100, pollInterval: 5000 });

  // Encompass data
  const {
    attachments: _encompassAttachments,
    metrics: encompassMetrics,
    downloadStats,
    loading: encompassLoading,
    error: encompassError,
    refresh: refreshEncompass,
    getFilteredAttachments,
    getGroupedFilteredAttachments
  } = useEncompassExtractionData({ loanId, limit: 500, pollInterval: 5000 });

  const [filter, setFilter] = useState<FilterType>('all');
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  // Get current data based on source
  const metrics = source === 'lendwise' ? lendwiseMetrics : encompassMetrics;
  const loading = source === 'lendwise' ? lendwiseLoading : encompassLoading;
  const error = source === 'lendwise' ? lendwiseError : encompassError;
  const refresh = source === 'lendwise' ? refreshLendwise : refreshEncompass;

  const filteredLendwiseDocs = getFilteredDocuments(filter);
  const filteredEncompassAttachments = getFilteredAttachments(filter);

  // Get grouped data for current filter
  const groupedLendwiseDocs = getGroupedFilteredDocuments(filter);
  const groupedEncompassAttachments = getGroupedFilteredAttachments(filter);

  const unprocessedLendwiseDocs = getFilteredDocuments('unprocessed');
  const unprocessedEncompassAttachments = getFilteredAttachments('unprocessed');

  // Handle Lendwise document processing
  const handleProcessLendwise = async (docId: string) => {
    setProcessingIds((prev) => new Set(prev).add(docId));
    try {
      await queueDocumentForProcessing(docId);
      toast({
        title: 'Document queued',
        description: 'The document has been added to the processing queue.'
      });
      refreshLendwise();
    } catch (err) {
      toast({
        title: 'Failed to queue document',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive'
      });
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    }
  };

  // Handle Encompass attachment processing
  const handleProcessEncompass = async (attachmentId: string) => {
    setProcessingIds((prev) => new Set(prev).add(attachmentId));
    try {
      await queueEncompassAttachmentForProcessing(attachmentId);
      toast({
        title: 'Attachment queued',
        description: 'The Encompass attachment has been added to the processing queue.'
      });
      refreshEncompass();
    } catch (err) {
      toast({
        title: 'Failed to queue attachment',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive'
      });
    } finally {
      setProcessingIds((prev) => {
        const next = new Set(prev);
        next.delete(attachmentId);
        return next;
      });
    }
  };

  // Handle bulk processing
  const handleBulkProcess = async () => {
    setBulkProcessing(true);
    try {
      if (source === 'lendwise') {
        if (unprocessedLendwiseDocs.length === 0) {
return;
}
        const docIds = unprocessedLendwiseDocs.map((d) => d.id);
        const result = await queueDocumentsForProcessing(docIds);
        toast({
          title: 'Documents queued',
          description: `${result.queued} queued, ${result.skipped} skipped, ${result.failed} failed`
        });
        refreshLendwise();
      } else {
        if (unprocessedEncompassAttachments.length === 0) {
return;
}
        const attachmentIds = unprocessedEncompassAttachments.map((a) => a.id);
        const result = await queueEncompassAttachmentsForProcessing(attachmentIds);
        toast({
          title: 'Attachments queued',
          description: `${result.queued} queued, ${result.skipped} skipped, ${result.failed} failed`
        });
        refreshEncompass();
      }
    } catch (err) {
      toast({
        title: 'Failed to queue documents',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive'
      });
    } finally {
      setBulkProcessing(false);
    }
  };

  const unprocessedCount = source === 'lendwise' ? unprocessedLendwiseDocs.length : unprocessedEncompassAttachments.length;

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
          <p className="text-red-600 font-medium">Failed to load extraction data</p>
          <p className="text-sm text-muted-foreground mt-1">{error}</p>
          <Button variant="outline" onClick={refresh} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      {/* Source Toggle */}
      <div className="px-6 py-3 border-b bg-white">
        <Tabs value={source} onValueChange={(v) => {
 setSource(v as SourceType); setFilter('all'); 
}}>
          <TabsList>
            <TabsTrigger value="lendwise" className="gap-2">
              <HardDrive className="h-4 w-4" />
              Lendwise Uploads ({lendwiseDocs.length})
            </TabsTrigger>
            <TabsTrigger value="encompass" className="gap-2">
              <Cloud className="h-4 w-4" />
              Encompass eFolder ({downloadStats.downloaded} downloaded)
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Encompass Download Stats (only for Encompass source) */}
      {source === 'encompass' && (
        <div className="px-6 py-2 border-b bg-blue-50/50 flex items-center gap-4 text-sm">
          <span className="text-muted-foreground">Download Status:</span>
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 text-green-600" />
            <span className="font-medium text-green-700">{downloadStats.downloaded}</span> downloaded
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3 text-yellow-600" />
            <span className="font-medium text-yellow-700">{downloadStats.pending}</span> pending
          </span>
          {downloadStats.failed > 0 && (
            <span className="flex items-center gap-1">
              <XCircle className="h-3 w-3 text-red-600" />
              <span className="font-medium text-red-700">{downloadStats.failed}</span> failed
            </span>
          )}
          {downloadStats.unavailable > 0 && (
            <span className="flex items-center gap-1">
              <CloudOff className="h-3 w-3 text-gray-500" />
              <span className="font-medium text-gray-600">{downloadStats.unavailable}</span> unavailable
            </span>
          )}
        </div>
      )}

      {/* Metrics Cards */}
      <div className="px-6 py-4 border-b bg-gray-50">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <MetricCard
            title="Total"
            value={<div className="text-2xl font-bold">{metrics.total}</div>}
            gpuStats={metrics.gpuStats}
          />

          <MetricCard
            title="Unprocessed"
            icon={<FileQuestion className="h-3 w-3" />}
            highlight={metrics.unprocessed > 0 ? 'warning' : undefined}
            value={
              <div className={cn('text-2xl font-bold', metrics.unprocessed > 0 && 'text-orange-600')}>
                {metrics.unprocessed}
              </div>
            }
            gpuStats={metrics.gpuStats}
          />

          <MetricCard
            title="Pending"
            icon={<Clock className="h-3 w-3" />}
            value={<div className="text-2xl font-bold text-yellow-600">{metrics.pending}</div>}
            gpuStats={metrics.gpuStats}
          />

          <MetricCard
            title="Processing"
            icon={<Loader2 className="h-3 w-3" />}
            value={<div className="text-2xl font-bold text-blue-600">{metrics.processing}</div>}
            gpuStats={metrics.gpuStats}
          />

          <MetricCard
            title="Completed"
            icon={<CheckCircle2 className="h-3 w-3" />}
            value={<div className="text-2xl font-bold text-green-600">{metrics.completed}</div>}
            gpuStats={metrics.gpuStats}
          />

          <MetricCard
            title="Avg Extract"
            icon={<Cpu className="h-3 w-3" />}
            value={
              <div className="text-2xl font-bold tabular-nums">
                {formatMs(metrics.avgExtractionTimeMs)}
              </div>
            }
            gpuStats={metrics.gpuStats}
          />

          <MetricCard
            title="Avg Classify"
            icon={<Server className="h-3 w-3" />}
            value={
              <div className="text-2xl font-bold tabular-nums">
                {formatMs(metrics.avgClassificationTimeMs)}
              </div>
            }
            gpuStats={metrics.gpuStats}
          />

          <MetricCard
            title="Avg Total"
            icon={<Timer className="h-3 w-3" />}
            value={
              <div className="text-2xl font-bold tabular-nums">
                {formatMs(metrics.avgTotalTimeMs)}
              </div>
            }
            gpuStats={metrics.gpuStats}
          />
        </div>
      </div>

      {/* Filter Tabs + Grouping Toggle + Bulk Action */}
      <div className="px-6 py-3 border-b flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterType)}>
            <TabsList>
              <TabsTrigger value="all">
                All ({metrics.total})
              </TabsTrigger>
              <TabsTrigger value="unprocessed" className={cn(metrics.unprocessed > 0 && 'text-orange-600')}>
                Unprocessed ({metrics.unprocessed})
              </TabsTrigger>
              <TabsTrigger value="processing">
                Processing ({metrics.pending + metrics.processing})
              </TabsTrigger>
              <TabsTrigger value="completed">
                Completed ({metrics.completed})
              </TabsTrigger>
              <TabsTrigger value="failed" className={cn(metrics.failed > 0 && 'text-red-600')}>
                <XCircle className="h-3 w-3 mr-1" />
                Failed ({metrics.failed})
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Grouping Mode Toggle */}
          <Select
            value={groupingMode}
            onValueChange={(v) => setGroupingMode(v as ExtractionGroupingMode)}
          >
            <SelectTrigger className="w-[130px] h-8">
              <Layers className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="flat">Flat View</SelectItem>
              <SelectItem value="groups">Grouped</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {unprocessedCount > 0 && (
          <Button
            variant="default"
            size="sm"
            onClick={handleBulkProcess}
            disabled={bulkProcessing}
          >
            {bulkProcessing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Process All ({unprocessedCount})
              </>
            )}
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : source === 'lendwise' ? (
          // Lendwise table
          filteredLendwiseDocs.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <div className="text-center">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                {filter === 'all' ? (
                  <>
                    <p>No documents found</p>
                    <p className="text-sm">No documents available for extraction</p>
                  </>
                ) : filter === 'unprocessed' ? (
                  <>
                    <p>All documents have been processed</p>
                    <p className="text-sm">No documents waiting for extraction</p>
                  </>
                ) : filter === 'processing' ? (
                  <>
                    <p>No documents currently processing</p>
                    <p className="text-sm">Queue some unprocessed documents to see them here</p>
                  </>
                ) : filter === 'completed' ? (
                  <>
                    <p>No completed extractions yet</p>
                    <p className="text-sm">Processed documents will appear here</p>
                  </>
                ) : (
                  <>
                    <p>No failed extractions</p>
                    <p className="text-sm">Documents that fail processing will appear here</p>
                  </>
                )}
              </div>
            </div>
          ) : groupingMode === 'groups' && groupedLendwiseDocs.length > 0 ? (
            // Grouped view for Lendwise
            <GroupedExtractionTable
              groups={groupedLendwiseDocs}
              source="lendwise"
              renderRow={(doc) => (
                <LendwiseDocumentRow
                  key={doc.id}
                  doc={doc}
                  onProcess={handleProcessLendwise}
                  isProcessing={processingIds.has(doc.id)}
                />
              )}
            />
          ) : (
            // Flat view for Lendwise
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[250px]">Document</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="w-[80px]">Conf.</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="w-[100px]">GPU</TableHead>
                  <TableHead className="w-[80px] text-right">Time</TableHead>
                  <TableHead className="w-[60px] text-right">Pages</TableHead>
                  <TableHead className="w-[80px] text-right">Chars</TableHead>
                  <TableHead className="w-[120px]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLendwiseDocs.map((doc) => (
                  <LendwiseDocumentRow
                    key={doc.id}
                    doc={doc}
                    onProcess={handleProcessLendwise}
                    isProcessing={processingIds.has(doc.id)}
                  />
                ))}
              </TableBody>
            </Table>
          )
        ) : (
          // Encompass table
          filteredEncompassAttachments.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <div className="text-center">
                <Cloud className="h-12 w-12 mx-auto mb-2 opacity-50" />
                {filter === 'all' ? (
                  downloadStats.downloaded === 0 ? (
                    <>
                      <p>No downloaded Encompass attachments</p>
                      <p className="text-sm">Wait for attachments to download or sync from Encompass eFolder tab</p>
                    </>
                  ) : (
                    <>
                      <p>No Encompass attachments found</p>
                      <p className="text-sm">Sync documents from the Encompass eFolder tab</p>
                    </>
                  )
                ) : filter === 'unprocessed' ? (
                  <>
                    <p>All downloaded attachments have been processed</p>
                    <p className="text-sm">No attachments waiting for extraction</p>
                  </>
                ) : filter === 'processing' ? (
                  <>
                    <p>No attachments currently processing</p>
                    <p className="text-sm">Queue some unprocessed attachments to see them here</p>
                  </>
                ) : filter === 'completed' ? (
                  <>
                    <p>No completed extractions yet</p>
                    <p className="text-sm">Processed attachments will appear here</p>
                  </>
                ) : (
                  <>
                    <p>No failed extractions</p>
                    <p className="text-sm">Attachments that fail processing will appear here</p>
                  </>
                )}
              </div>
            </div>
          ) : groupingMode === 'groups' && groupedEncompassAttachments.length > 0 ? (
            // Grouped view for Encompass
            <GroupedExtractionTable
              groups={groupedEncompassAttachments}
              source="encompass"
              renderRow={(attachment) => (
                <EncompassAttachmentRow
                  key={attachment.id}
                  attachment={attachment}
                  onProcess={handleProcessEncompass}
                  isProcessing={processingIds.has(attachment.id)}
                />
              )}
            />
          ) : (
            // Flat view for Encompass
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[45%]">Document</TableHead>
                  <TableHead className="w-[35%]">Status</TableHead>
                  <TableHead className="w-[10%] text-right">Time</TableHead>
                  <TableHead className="w-[10%] text-right">Chars</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEncompassAttachments.map((attachment) => (
                  <EncompassAttachmentRow
                    key={attachment.id}
                    attachment={attachment}
                    onProcess={handleProcessEncompass}
                    isProcessing={processingIds.has(attachment.id)}
                  />
                ))}
              </TableBody>
            </Table>
          )
        )}
      </div>

      {/* Footer */}
      <div className="border-t bg-white px-6 py-2 flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {source === 'lendwise'
            ? `${filteredLendwiseDocs.length} documents`
            : `${filteredEncompassAttachments.length} attachments`}
          {metrics.failed > 0 && (
            <span className="text-red-600 ml-2">({metrics.failed} failed)</span>
          )}
        </span>
        <Button variant="ghost" size="sm" onClick={refresh} className="h-7">
          <RefreshCw className="h-3 w-3 mr-1" />
          Refresh
        </Button>
      </div>
    </div>
  );
}
